import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Edit, FileText, Link as LinkIcon, Loader2, Paperclip, Printer, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import FilePreviewModal from '../components/FilePreviewModal';
import CaseHistory from '../components/CaseHistory';
import { formatDate, formatDateTime } from '../utils/helpers';
import { exportCaseToExcel } from '../utils/exportUtils';


const getOfficeHeading = (name) => {
  if (!name) return 'Office';
  const trimmed = String(name).trim();
  if (!trimmed) return 'Office';
  if (/^office of\b/i.test(trimmed)) return trimmed;
  return `Office of ${trimmed}`;
};

const htmlToText = (html) => {
  const raw = String(html || '');
  if (!raw) return '';
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h1|h2|h3|h4|h5|h6|tr)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const Field = ({ label, value }) => (
  <div className="space-y-1">
    <div className="text-xs font-medium text-gray-500">{label}</div>
    <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">{value || '-'}</div>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-center gap-3">
      <div className="p-2 bg-indigo-50 rounded-lg">
        {Icon ? <Icon className="w-5 h-5 text-indigo-600" /> : null}
      </div>
      <div className="text-sm font-semibold text-gray-800">{title}</div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const ReceiptView = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { get, post } = useApi();
  const { user } = useAuth();
  const { settings } = useSettings();
  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;

  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState(null);
  const [existingFiles, setExistingFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [office, setOffice] = useState(null);
  const [linkedCases, setLinkedCases] = useState([]);
  const [complianceLogs, setComplianceLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const previewRef = useRef(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      setLoading(true);
      try {
        const response = await get(`/receipt/${id}`);
        setReceipt(response.data);
        setExistingFiles(response.data.attachments || []);
      } catch (e) {
        toast.error('Failed to load diary details');
        navigate('/diary');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchReceipt();
  }, [get, id, navigate]);

  useEffect(() => {
    if (loading) return;
    if (!id) return;
    const qs = new URLSearchParams(location.search || '');
    if (qs.get('print') !== '1') return;
    const t = setTimeout(() => {
      try {
        window.print();
      } catch {
        void 0;
      }
    }, 400);
    return () => clearTimeout(t);
  }, [id, loading, location.search]);

  useEffect(() => {
    (async () => {
      if (!id) return;
      try {
        const res = await get(`/relations/receipt/${id}`);
        const all = Array.isArray(res?.data) ? res.data : [];
        const outgoing = all.filter(r => !String(r?.relationType || '').includes('(Incoming)'));
        setLinkedCases(outgoing);
      } catch (e) {
        void e;
        setLinkedCases([]);
      }
    })();
  }, [get, id]);

  useEffect(() => {
    const fetchOffice = async () => {
      if (!user?.officeId) return;
      try {
        const response = await get(`/offices/${user.officeId}`);
        setOffice(response.data);
      } catch (e) {
        setOffice(null);
      }
    };
    fetchOffice();
  }, [get, user?.officeId]);

  useEffect(() => {
    if (!id) return;
    const fetchLogs = async () => {
      try {
        const response = await get(`/compliance/logs?letterType=receipt&letterId=${id}`);
        setComplianceLogs(Array.isArray(response?.data) ? response.data : []);
      } catch {
        setComplianceLogs([]);
      }
    };
    fetchLogs();
  }, [get, id]);

  const pdfFiles = useMemo(
    () => existingFiles.filter(f => f?.mimeType === 'application/pdf'),
    [existingFiles]
  );

  const handleDownload = async (file) => {
    try {
      const blob = await get(`/files/${file.fileName}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDownloadBundle = async () => {
    try {
      if (pdfFiles.length === 0) return;
      const fileIds = pdfFiles.map(f => f.id);
      const bundleName = `Diary_${receipt?.diaryNumber || receipt?.receiptNumber || id}.pdf`;
      const response = await post('/pdf/bundle', { fileIds, bundleName }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', bundleName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('PDF downloaded');
    } catch (e) {
      console.error('Bundle failed', e);
      toast.error('Failed to download PDF');
    }
  };

  const handleDownloadCasePdf = async () => {
    try {
      const timestamp = Date.now();
      const response = await get(`/pdf/case/receipt/${id}?t=${timestamp}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = `Diary_${receipt?.diaryNumber || receipt?.receiptNumber || id}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('PDF downloaded successfully');
    } catch (e) {
      console.error('PDF export failed', e);
      toast.error('Failed to export PDF');
    }
  };

  const handleDownloadCaseExcel = () => {
    try {
      if (!receipt) return;
      const fileName = `Diary_${receipt?.diaryNumber || receipt?.receiptNumber || id}`;
      exportCaseToExcel({
        ...receipt,
        officeName: office?.name || user?.officeName || 'Office',
        diaryNumber: receipt?.diaryNumber || receipt?.receiptNumber || ''
      }, fileName);
      toast.success('Excel downloaded successfully');
    } catch (e) {
      console.error('Excel export failed', e);
      toast.error('Failed to export Excel');
    }
  };

  const getLinkHref = (link) => {
    if (!link?.type || !link?.id) return null;
    if (link.type === 'dispatch') return `/dispatch/${link.id}/view`;
    if (link.type === 'receipt') return `/diary/${link.id}/view`;
    if (link.type === 'efile') return `/efile/${link.id}`;
    if (link.type === 'efile_noting') return `/efile/${link.efileId || link.id}`;
    return null;
  };

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'attachments', label: 'Attachments' },
    { key: 'links', label: 'Linked Cases' },
    { key: 'history', label: 'History' },
  ];

  const renderOverview = () => (
    <>
      <Section title="Diary Details" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Subject" value={receipt.subject} />
          <Field label="Diary No." value={diaryNumber} />
          <Field label="Last Number / Serial" value={diaryNumberLastDigits} />
          <Field label="Date Received" value={formatDate(receipt.dateReceived)} />
          <Field label="Date of Letter" value={formatDate(receipt.dateOfLetter)} />
          <Field label="Reference No. / Letter No." value={receipt.referenceNumber} />
          <Field label="Document Type" value={receipt.documentType} />
          <Field label="Confidential" value={receipt.isConfidential ? 'Yes' : 'No'} />
          <Field label="Confidentiality Level" value={receipt.confidentialityLevel} />
        </div>
      </Section>

      <Section title="Sender Info" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Sender" value={receipt.sender} />
          <Field label="Sender Department" value={receipt.senderDept} />
          <Field label="Sender Address" value={receipt.senderAddress || receipt.address} />
          <Field label="Reference No. / Letter No." value={receipt.referenceNumber} />
          <Field label="Date of Letter" value={formatDate(receipt.dateOfLetter)} />
          <Field label="Action Required" value={receipt.actionRequired} />
          <div className="md:col-span-2">
            <Field label="Remarks / Other Details" value={htmlToText(receipt.remarks)} />
          </div>
        </div>
      </Section>

      <Section title="Forwarding & Status" icon={ShieldCheck}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Assigned To / Forward To" value={receipt.forwardedToName || receipt.forwardedTo || 'Central Registry'} />
          <Field label="Forward Date" value={receipt.forwardedAt ? formatDateTime(receipt.forwardedAt) : '-'} />
          <Field label="Current Status" value={receipt.status} />
          <Field label="Priority" value={receipt.priority} />
          <Field label="Date of Reversal" value={reversalDate ? formatDateTime(reversalDate) : '-'} />
          <Field label="Approval Status" value={receipt.approvalStatus} />
        </div>
      </Section>

      <Section title="Compliance Chain" icon={ShieldCheck}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Latest Officer / Compliance Officer" value={receipt.lastComplianceOfficerName || receipt.forwardedToName || '-'} />
          <Field label="Latest Office Diary Number" value={receipt.lastComplianceDiaryNumber || '-'} />
          <Field label="Latest Office Diary Date" value={receipt.lastComplianceDiaryDate ? formatDate(receipt.lastComplianceDiaryDate) : '-'} />
          <Field label="Compliance Entries" value={complianceLogs.length ? `${complianceLogs.length} recorded` : 'No compliance entries yet'} />
        </div>

        {complianceLogs.length > 0 && (
          <div className="mt-6 overflow-hidden rounded-lg border border-gray-200">
            <div className="bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 border-b border-gray-200">
              Recent Compliance Entries
            </div>
            <div className="divide-y divide-gray-200">
              {complianceLogs.slice(0, 5).map((log) => (
                <div key={log.id} className="p-4 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-800">
                      {log.actionTaken || 'Compliance Update'}
                    </span>
                    <span className="text-xs text-gray-500">{formatDateTime(log.updatedAt)}</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">Officer:</span> {log.officerName || receipt.forwardedToName || receipt.lastComplianceOfficerName || 'Unknown'}
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">Diary:</span> {log.diaryNumber || '-'}
                    {log.diaryDate ? ` • ${formatDate(log.diaryDate)}` : ''}
                  </div>
                  {log.remarks && (
                    <div className="text-sm text-gray-600 border-l-2 border-indigo-200 pl-3">
                      {log.remarks}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>
    </>
  );

  const renderAttachments = () => (
    <Section title="Attachments" icon={Paperclip}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm text-gray-600">{existingFiles.length} files</div>
        <div className="no-print flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={handleDownloadBundle}
            disabled={pdfFiles.length === 0}
            className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm disabled:opacity-50"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF Bundle
          </Button>
        </div>
      </div>

      {existingFiles.length === 0 ? (
        <div className="text-sm text-gray-500">No attachments uploaded.</div>
      ) : (
        <div className="space-y-2">
          {existingFiles.map((file) => (
            <div key={file.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 bg-white">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate" title={file.originalName}>{file.originalName}</div>
                <div className="text-xs text-gray-500">{file.mimeType}</div>
              </div>
              <div className="no-print flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="secondary"
                  onClick={() => { setPreviewFile(file); setIsPreviewOpen(true); }}
                  className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
                >
                  Preview
                </Button>
                <Button
                  onClick={() => handleDownload(file)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                >
                  Download
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );

  const renderLinks = () => (
    <Section title="Reference / Linked Cases" icon={LinkIcon}>
      {linkedCases.length === 0 ? (
        <div className="text-sm text-gray-500">No linked reference cases.</div>
      ) : (
        <div className="space-y-2">
          {linkedCases.map((link, idx) => {
            const href = getLinkHref(link);
            return (
              <div key={`${link.type}-${link.id}-${idx}`} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 bg-white">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {link.number || `${String(link.type).toUpperCase()} #${link.id}`}
                    {link.relationType ? <span className="text-gray-500 font-normal"> · {link.relationType}</span> : null}
                  </div>
                  <div className="text-xs text-gray-500 break-words">
                    {link.subject || 'No subject'} · {link.date ? formatDate(link.date) : 'No date'} · {link.status || 'No status'}
                  </div>
                </div>
                <div className="no-print flex-shrink-0">
                  {href ? (
                    <Button
                      variant="secondary"
                      onClick={() => navigate(href)}
                      className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
                    >
                      View
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );

  const renderHistory = () => (
    <div className="space-y-4">
      <CaseHistory caseType="receipt" caseId={id} />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!receipt) return null;

  const diaryNumber = receipt.diaryNumber || receipt.receiptNumber || `#${receipt.id}`;
  const diaryNumberLastDigits = String(diaryNumber).match(/(\d+)(?!.*\d)/)?.[1] || diaryNumber;
  const reversalDate = (() => {
    const reversalLog = (complianceLogs || []).find((log) => {
      const action = String(log?.actionTaken || '').toLowerCase();
      return ['reverted', 'withdrawn', 'returned', 'reversal'].some(term => action.includes(term));
    });
    return reversalLog?.updatedAt || receipt?.dateOfReversal || '';
  })();

  return (
    <>
      <style>{`@media print{
        .no-print{display:none !important}
        header, nav, aside{display:none !important}
        .w-64.bg-slate-900{display:none !important}
        body{background:#fff !important}
        .absolute.inset-0{position:static !important; inset:auto !important; overflow:visible !important}
        main{overflow:visible !important; padding:0 !important}
        .max-w-5xl{max-width:100% !important; margin:0 !important}
      }`}</style>

      <div className="max-w-5xl mx-auto mb-12">
        <div className="no-print flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/diary')}
              className="hover:bg-gray-100 rounded-full p-2 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Diary Preview</h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-medium px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full">
                    {diaryNumber}
                  </span>
                  {receipt.priority ? <Badge priority={receipt.priority}>{receipt.priority}</Badge> : null}
                </div>
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Created: {formatDateTime(receipt.createdAt)} · Updated: {formatDateTime(receipt.updatedAt)}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => window.print()}
              className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button
              variant="secondary"
              onClick={handleDownloadCasePdf}
              className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button
              variant="secondary"
              onClick={handleDownloadCaseExcel}
              className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Excel
            </Button>
            {isEfileEnabled && (
              <Button
                variant="secondary"
                onClick={() => navigate(`/green-sheet/${id}`)}
                className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
              >
                <FileText className="w-4 h-4 mr-2" />
                Green Sheet
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => navigate(`/diary/${id}`)}
              className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </div>
        </div>

        <div ref={previewRef} className="space-y-6">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 via-sky-900 to-indigo-900 text-white shadow-sm">
            <div className="relative p-6">
              <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-amber-400/20 to-transparent" />
              <div className="relative flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/40 bg-white/10 text-[10px] font-black tracking-[0.24em] text-white/90">
                  GOP
                </div>
                <div className="flex-1 text-center">
                  <div className="text-lg font-black uppercase tracking-[0.18em] text-white">WATER AND POWER DEVELOPMENT</div>
                  <div className="mt-1 text-base font-semibold tracking-wide text-sky-100">
                    {getOfficeHeading(office?.name || user?.officeName)}
                  </div>
                  <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-200">
                    Official Case Record
                  </div>
                  <div className="mt-2 text-[11px] text-sky-100/90">
                    {office?.code || user?.officeCode ? `${office?.code || user?.officeCode}` : ''}
                    {(office?.code || user?.officeCode) && office?.address ? ' · ' : ''}
                    {office?.address || ''}
                  </div>
                </div>
                <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/30 bg-white/10 text-[10px] font-bold tracking-[0.18em] text-sky-100">
                  WAPDA
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.key
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'attachments' && renderAttachments()}
          {activeTab === 'links' && renderLinks()}
          {activeTab === 'history' && renderHistory()}
        </div>
      </div>

      <FilePreviewModal
        file={previewFile}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </>
  );
};

export default ReceiptView;
